import { ChevronDownIcon } from '@langchain/untitled-ui-icons';
import { Slider } from '@mui/joy';

import { Dispatch, FC, RefObject, SetStateAction } from 'react';

import Chip from '@/components/Chip';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/Popover';
import {
  EQuickAdjustment,
  TArtifactLengthOptions,
  TReadingLevelOptions,
  adjustmentToComponentProps,
} from '@/hooks/promptCanvas/types';

type TProps = {
  quickAdjustmentType: EQuickAdjustment | undefined;
  setQuickAdjustmentType: Dispatch<
    SetStateAction<EQuickAdjustment | undefined>
  >;
  sliderValue: number;
  setSliderValue: Dispatch<SetStateAction<number>>;
  onSubmit: (value?: TArtifactLengthOptions | TReadingLevelOptions) => void;
  sliderRef: RefObject<HTMLDivElement>;
  disabled: boolean;
};

const DefaultQuickActions: FC<TProps> = ({
  quickAdjustmentType,
  setQuickAdjustmentType,
  sliderValue,
  setSliderValue,
  onSubmit,
  sliderRef,
  disabled,
}) => {
  const isDefaultQuickAction = (q: EQuickAdjustment) =>
    q !== EQuickAdjustment.CUSTOM;
  const handleSubmitSlider = () => {
    if (!quickAdjustmentType) return;
    // 0 is the default value, so we don't need to submit anything
    if (sliderValue === 0) return;
    // here we convert the slider value to the actual option to submit.

    const options = adjustmentToComponentProps[quickAdjustmentType].options;
    let index: number;
    // eg: slider values [-2, -1, 0, 1, 2] must map to options ["a", "b", "c", "d"]
    // so -2 -> "a", -1 -> "b", 1 -> "c", 2 -> "d", hence the following logic:
    if (sliderValue > 0) {
      index = sliderValue + 1;
    } else {
      index = sliderValue + 2;
    }
    const value = options[index];
    onSubmit(value);
    setQuickAdjustmentType(undefined);
    setSliderValue(0);
  };
  return (
    <Popover
      open={!!quickAdjustmentType && isDefaultQuickAction(quickAdjustmentType)}
    >
      <PopoverTrigger className="flex flex-row flex-wrap gap-1">
        {Object.values(EQuickAdjustment)
          .filter((adjustment) => adjustment !== EQuickAdjustment.CUSTOM)
          .map((adjustment) => (
            <div key={adjustment} className="shrink-0">
              <Chip
                label={adjustment}
                onClick={() => {
                  setQuickAdjustmentType((prev) => {
                    if (prev === adjustment) return undefined;
                    return adjustment;
                  });
                }}
                rightIcon={
                  <ChevronDownIcon className="h-4 w-4 text-secondary" />
                }
              />
            </div>
          ))}
      </PopoverTrigger>
      <PopoverContent className="w-[400px] p-0">
        <div
          className="mb-1 flex justify-center px-2 pb-4 pt-1"
          ref={sliderRef}
          onMouseUp={handleSubmitSlider}
        >
          <Slider
            orientation="horizontal"
            value={sliderValue}
            step={1}
            min={-2}
            max={2}
            marks={
              quickAdjustmentType && isDefaultQuickAction(quickAdjustmentType)
                ? adjustmentToComponentProps[quickAdjustmentType].marks
                : undefined
            }
            onChange={(_, value) => setSliderValue(value as number)}
            disabled={disabled}
            sx={{
              '--Slider-trackSize': '8px',
              width: '250px',
            }}
          />
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default DefaultQuickActions;
